/**
 * Desafio do segundo fator, no login. (BKC-012)
 *
 * Fora do AppShell de propósito: a sessão está pendente e a API recusa tudo o
 * mais. Oferecer os menus aqui prometeria acesso que o servidor vai negar, e a
 * pessoa concluiria que o sistema está quebrado — o mesmo raciocínio da tela de
 * troca de senha obrigatória.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimboloCeres } from '../components/Marca';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { useSession } from '../lib/session';
import { Field, FormErrorSummary, InlineError, Spinner, useToast } from '../components/ui';

export function MfaChallengePage(): JSX.Element {
  const { user, refresh, logout } = useSession();
  const { notify } = useToast();
  const navigate = useNavigate();

  const [modo, setModo] = useState<'CODIGO' | 'RECUPERACAO'>('CODIGO');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const enviar = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (modo === 'CODIGO') {
        await api.post('/auth/mfa/verify', { code });
        notify('success', 'Segundo fator verificado.');
      } else {
        const body = await api.post<{ recoveryCodesRemaining: number }>('/auth/mfa/recovery', {
          recoveryCode,
        });
        notify(
          'success',
          `Código de recuperação aceito. Restam ${body.recoveryCodesRemaining}. ` +
            'Cadastre o aplicativo autenticador novamente.',
        );
      }
      // Recarrega a sessão para que a interface saiba que o desafio caiu — e,
      // no caminho da recuperação, que agora falta cadastrar o aplicativo.
      await refresh();
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <SimboloCeres className="mb-3 h-11 w-11 text-brand-600" decorativa />
          <h1 className="text-xl font-semibold text-ink">{t('segundoFator.confirme')}</h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            {user?.name ?? 'Sua conta'} usa segundo fator de autenticação.
          </p>
        </div>

        <form onSubmit={enviar} className="panel space-y-4 px-4 py-4">
          {modo === 'CODIGO' ? (
            <Field
              label={t('segundoFator.codigoApp')}
              htmlFor="mfa-code"
              required
              hint="Os 6 dígitos que aparecem no seu aplicativo autenticador."
            >
              <input
                id="mfa-code"
                data-testid="mfa-code"
                className="field-input text-center text-lg tracking-[0.3em] tabular-nums"
                // `one-time-code` faz o teclado do celular e o preenchimento
                // automático do sistema oferecerem o código certo.
                autoComplete="one-time-code"
                inputMode="numeric"
                autoFocus
                maxLength={7}
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>
          ) : (
            <Field
              label={t('segundoFator.codigoRecuperacao')}
              htmlFor="mfa-recovery"
              required
              hint="Um dos códigos que você guardou ao cadastrar o segundo fator. Cada um serve uma única vez."
            >
              <input
                id="mfa-recovery"
                data-testid="mfa-recovery"
                className="field-input text-center uppercase tracking-widest"
                autoComplete="off"
                autoFocus
                maxLength={14}
                placeholder="ABCDE-FGHJK"
                value={recoveryCode}
                onChange={(event) => setRecoveryCode(event.target.value)}
              />
            </Field>
          )}

          <FormErrorSummary error={error} />
          <InlineError error={error} />

          <button
            type="submit"
            className="btn-primary w-full"
            data-testid="mfa-enviar"
            disabled={busy || (modo === 'CODIGO' ? code.trim().length < 6 : recoveryCode.trim().length < 8)}
          >
            {busy && <Spinner />}
            Entrar
          </button>

          <div className="flex flex-col gap-1.5 border-t border-line pt-3 text-center text-xs">
            <button
              type="button"
              className="text-brand-700 hover:underline"
              onClick={() => {
                setModo(modo === 'CODIGO' ? 'RECUPERACAO' : 'CODIGO');
                setError(null);
              }}
            >
              {modo === 'CODIGO'
                ? 'Perdi o acesso ao aplicativo — usar código de recuperação'
                : 'Voltar a usar o código do aplicativo'}
            </button>
            <button
              type="button"
              className="text-ink-muted hover:underline"
              onClick={() => {
                void logout().then(() => navigate('/entrar', { replace: true }));
              }}
            >{t('segundoFator.sairOutraConta')}</button>
          </div>
        </form>

        <p className="mt-3 text-center text-xs text-ink-subtle">{t('segundoFator.semOsDois')}</p>

      </div>
    </div>
  );
}
